import React, { createContext, PropsWithChildren, useState } from "react"

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  IAuthenticationCallback,
  NodeCallback
} from "amazon-cognito-identity-js"

const userPoolId = import.meta.env.VITE_USER_POOL_ID as string
const cognitoClientId = import.meta.env.VITE_CLIENT_ID as string

export type AuthContextProps = {
  userId: string
  clientId: string
  availableClientIds: string[]
  tryGetStoredSession: () => Promise<string>
  switchClientId: (newClientId: string) => boolean
  authenticateUser: (email: string, password: string, callbacks: IAuthenticationCallback) => void
  completeNewPasswordChallenge(newPassword: string, callbacks: IAuthenticationCallback): void
  resetPassword: (email: string, callbacks: IAuthenticationCallback) => void
  confirmForgotPassword: (email: string, verificationCode: string, newPassword: string, callbacks: { onSuccess: (success: string) => void; onFailure: (err: Error) => void }) => void
  updatePassword: (oldPassword: string, newPassword: string, callback: NodeCallback<Error, 'SUCCESS'>) => void
  logout: () => void
}

export const AuthContext = createContext<AuthContextProps>({
  userId: "",
  clientId: "",
  availableClientIds: [],
  tryGetStoredSession: () => new Promise(() => ""),
  switchClientId: () => { return false },
  authenticateUser: () => { },
  completeNewPasswordChallenge: () => { },
  resetPassword: () => { },
  updatePassword: () => { },
  confirmForgotPassword: () => { },
  logout: () => { },
})

/**
 * A context which provides information about the logged in user
 */
export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {

  const [userSession, setUserSession] = useState<CognitoUserSession | null>(null)
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null)
  const [userId, setUserId] = useState<string>("")
  const [clientId, setClientId] = useState<string>("")
  const [availableClientIds, setAvailableClientIds] = useState<string[]>([])

  const userPool: CognitoUserPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: cognitoClientId,
  })

  /*
   * Tries to get the stored session for the current user.
   * If no valid session is found, clears stored user data.
   */
  const tryGetStoredSession = async () => {
    const userSession = await tryGetUserSession()

    if (!userSession) {
      logout()
      return ""
    }

    return userSession.getIdToken().getJwtToken()
  }

  /**
   * Tries to get the current user's session from storage.
   * If the session is invalid or missing, returns null.
   */
  const tryGetUserSession = async (): Promise<CognitoUserSession | null> => {
    if (userSession && userSession.isValid()) {
      return userSession
    }

    // Do not want log information to remain between sessions
    const userName = sessionStorage.getItem("userName")
    const refreshToken = sessionStorage.getItem("refreshToken")
    const idToken = sessionStorage.getItem("idToken")
    const accessToken = sessionStorage.getItem("accessToken")

    if (!userName || !refreshToken || !idToken || !accessToken) {
      return null
    }

    const parsedRefreshToken = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    })

    const parsedIdToken = new CognitoIdToken({
      IdToken: idToken,
    })

    const parsedAccessToken = new CognitoAccessToken({
      AccessToken: accessToken,
    })

    const parsedSession = new CognitoUserSession({
      IdToken: parsedIdToken,
      AccessToken: parsedAccessToken,
      RefreshToken: parsedRefreshToken,
    })

    const cognitoUser = new CognitoUser({
      Username: userName,
      Pool: userPool,
    })

    if (!parsedSession.isValid()) {
      return null
    }

    if (!parsedSession.getAccessToken()) {
      return null
    }

    if (!parsedSession.getIdToken()) {
      return null
    }

    if (!parsedSession.getRefreshToken()) {
      return null
    }

    const session: CognitoUserSession | null = await refreshSession(
      cognitoUser,
      parsedSession)

    if (!session) {
      return null
    }

    return session
  }

  /**
   * Refreshes the current user's session using the provided Cognito user and session.
   * Returns the new session if successful, or null if the refresh fails.
   */
  const refreshSession = async (
    cognitoUser: CognitoUser,
    session: CognitoUserSession,
  ): Promise<CognitoUserSession | null> => {

    return await new Promise<CognitoUserSession | null>((resolve, reject) => {
      cognitoUser.refreshSession(session.getRefreshToken(), (err: unknown, session: any) => {
        if (err) {
          console.log("error refreshing session", err)
          reject(null)
        }

        if (session === null || session === undefined) {
          console.warn("Failed to refresh session")
          reject(null)
        }

        const result = setUserSessionIfValid(session, cognitoUser)
        if (result.success) {
          resolve(session)
        } else {
          console.log("Failed refresh session")
          reject(null)
        }
      })
    })
  }

  /**
   * Sets the user session if it is valid.
   * Returns an object indicating success or failure, along with user and client IDs.
   */
  const setUserSessionIfValid = (
    session: CognitoUserSession | null,
    user: CognitoUser | null,
  ): {
    success: boolean,
    userId: string,
    clientId: string,
    errorMessage?: string,
  } => {
    if (!session || !user) {
      console.warn("Invalid session or user data")
      logout()
      return {
        success: false,
        userId: "",
        clientId: "",
        errorMessage: "Failed to set session"
      }
    }

    const token = session.getIdToken()
    const userId = token.payload["custom:userId"] || ""

    const clientIdSet = token.payload["custom:clientIdSet"] || ""
    const availableClientIds: string[] = clientIdSet.split(",") || []

    const firstClientId = availableClientIds.length > 0 ? availableClientIds[0] : ""
    const lastClientId = localStorage.getItem("lastClientId")

    let clientId = firstClientId
    if (lastClientId && availableClientIds.includes(lastClientId)) {
      clientId = lastClientId
    }

    const refreshToken = session.getRefreshToken()
    const idToken = session.getIdToken()
    const accessToken = session.getAccessToken()

    if (!refreshToken || !idToken || !accessToken) {
      console.error("No tokens found in session")
      logout()
      return {
        success: false,
        userId: "",
        clientId: "",
        errorMessage: "No tokens found in session"
      }
    }

    setUserSession(session)
    setCognitoUser(user)
    setUserId(userId)
    setClientId(clientId)
    setAvailableClientIds(availableClientIds)

    sessionStorage.setItem("userName", user.getUsername())
    sessionStorage.setItem("refreshToken", refreshToken.getToken())
    sessionStorage.setItem("idToken", idToken.getJwtToken())
    sessionStorage.setItem("accessToken", accessToken.getJwtToken())

    localStorage.setItem("lastClientId", clientId)

    // Make sure there is no lingering data
    localStorage.removeItem("userName")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("idToken")
    localStorage.removeItem("accessToken")

    return {
      success: true,
      userId,
      clientId,
    }
  }

  const authenticateUser = (
    email: string,
    password: string,
    callbacks: IAuthenticationCallback
  ): void => {

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    })

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const result = setUserSessionIfValid(session, cognitoUser)
        if (result.success) {
          callbacks.onSuccess(session)
        } else {
          console.log("authenticateUser failed to set session")
          callbacks.onFailure(new Error(result.errorMessage || "Failed to set session"))
        }
      },
      onFailure: (err) => {
        console.log(err, "error")
        callbacks.onFailure(err)
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        setCognitoUser(cognitoUser)

        if (callbacks.newPasswordRequired) {
          callbacks.newPasswordRequired(userAttributes, requiredAttributes)
        }
      },
    })
  }

  /**
   * Completes the new password challenge for the current Cognito user.
   * Sets the new password and updates the session if successful.
   */
  const completeNewPasswordChallenge = (
    newPassword: string,
    callbacks: IAuthenticationCallback,
  ): void => {

    cognitoUser?.completeNewPasswordChallenge(
      newPassword,
      {}, {
      onSuccess: (session: CognitoUserSession) => {

        const result = setUserSessionIfValid(session, cognitoUser)
        if (result.success) {
          callbacks.onSuccess(session)
        } else {
          console.log("New Password: failed to set session")
          callbacks.onFailure(new Error(result.errorMessage || "Failed to set session"))
        }
      },
      onFailure: (err) => {
        if (err.code === "InvalidPasswordException") {
          throw Error(
            "Password must be at least 8 characters long and contain at least one number and one lowercase letter"
          )
        }
        console.log(err)
        callbacks.onFailure(err)
      },
    })
  }

  /**
   * Initiates the password reset process for the specified email.
   * Calls the appropriate callbacks based on success or failure.
   */
  const resetPassword = (email: string, callbacks: IAuthenticationCallback) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.forgotPassword({
      onSuccess: (result) => {
        callbacks.onSuccess(result)
      },
      onFailure: (err) => {
        callbacks.onFailure(err)
      },
    })
  }

  /**
   * Confirms the password reset for the specified email using the verification code and new password.
   * Calls the appropriate callbacks based on success or failure.
   */
  const confirmForgotPassword = (email: string, verificationCode: string, newPassword: string, callbacks: {
    onSuccess: (success: string) => void
    onFailure: (err: Error) => void
  }) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: (success: string) => {
        console.log(success)
        callbacks.onSuccess(success)
      },
      onFailure: (err) => {
        console.log(err)
        callbacks.onFailure(err)
      },
    })
  }

  /**
   * Updates the password for the current Cognito user.
   * Calls the provided callback with the result of the operation.
   */
  const updatePassword = (oldPassword: string, newPassword: string, callback: NodeCallback<Error, 'SUCCESS'>) => {

    if (!cognitoUser) {
      return
    }

    cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
      if (err) {
        console.log(err)
        callback(err)
      }
      else {
        callback()
      }
    })
  }

  /**
   * Logs out the current user by clearing session storage and signing out from Cognito.
   */
  const logout = () => {
    sessionStorage.removeItem("userName")
    sessionStorage.removeItem("refreshToken")
    sessionStorage.removeItem("idToken")
    sessionStorage.removeItem("accessToken")

    if (cognitoUser) {
      cognitoUser.signOut()
    }

    setUserId("")
  }
  /**
   * Switches the current client ID to the specified new client ID.
   * Returns true if the switch is successful, false otherwise.
   */
  const switchClientId = (newClientId: string) => {
    if (newClientId.length == 0) {
      return false
    }

    if (!availableClientIds.includes(newClientId)) {
      return false
    }

    localStorage.setItem("lastClientId", newClientId)
    setClientId(newClientId)

    return true
  }

  return (
    <AuthContext.Provider
      value={{
        userId,
        clientId,
        availableClientIds,
        tryGetStoredSession,
        switchClientId,
        authenticateUser,
        completeNewPasswordChallenge,
        resetPassword,
        confirmForgotPassword,
        updatePassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
